import React from 'react';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  accessTokenSelector,
  fetchingLogout,
  setAuthTokens,
} from '@/store/auth/authSlices';
import { profileSelector } from 'store/profile/profileSlices';
import type { IUser, UserRole, Permission } from 'types';
import { ROLE_PERMISSIONS } from 'types';

type AuthContextType = {
  accessToken: string | null;
  isAuthenticated: boolean;
  profile: IUser | null;
  role: UserRole | null;
  // Хелперы для проверки ролей
  isOwner: boolean;
  isManager: boolean;
  isTenant: boolean;
  // Проверка прав доступа
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  // Методы
  login: (token: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType>({
  accessToken: null,
  isAuthenticated: false,
  profile: null,
  role: null,
  isOwner: false,
  isManager: false,
  isTenant: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => {
  return useContext(AuthContext);
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useDispatch();
  const accessToken = useSelector(accessTokenSelector);
  const profile = useSelector(profileSelector);

  const isAuthenticated = !!accessToken;
  const role = profile?.role || null;

  // Хелперы для проверки ролей
  const isOwner = role === 'owner';
  const isManager = role === 'manager';
  const isTenant = role === 'tenant';

  // Проверка конкретного права доступа
  const hasPermission = (permission: Permission): boolean => {
    if (!role) return false;
    return ROLE_PERMISSIONS[role]?.includes(permission) || false;
  };

  // Проверка любого из прав доступа
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some((permission) => hasPermission(permission));
  };

  const login = (token: string) => {
    if (!token) {
      return;
    }
    localStorage.setItem('accessToken', token);
    dispatch(setAuthTokens(token));
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    dispatch(fetchingLogout());
  };

  const value = {
    accessToken,
    isAuthenticated,
    login,
    logout,
    profile,
    role,
    isOwner,
    isManager,
    isTenant,
    hasPermission,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
